import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';

// ---------------------------------------------------------------------------
// Module ID mapping: Analyze13 UI IDs → backend module keys (v1.3 schemas)
// ---------------------------------------------------------------------------
const MODULE_ID_MAP = {
  // My Podcast track
  keyword_analyzer:     'keyword_analyzer',
  episode_optimizer_v2: 'episode_optimizer_v2',
  health_check_v2:      'health_check_v2',
  contact_owner:        'contact_owner',
  social_audit:         'social_audit',
  cadence_v2:           'cadence_v2',
  theme_map_v2:         'theme_map_v2',
  op3_downloads:        'op3_downloads',      // handled via separate OP3 API endpoint
  sponsor_suggestions:  'sponsor_suggestions',
  // Research track
  category_themes:      'category_themes',
  guest_extractor:      'guest_extractor',
  social_scan_v2:       'social_scan_v2',
  contact_research:     'contact_research',
  sponsor_finder:       'sponsor_finder',
};

const EPISODE_LIMIT = 20;

// ---------------------------------------------------------------------------
// Thunks
// ---------------------------------------------------------------------------

/** Parse an RSS feed and return show metadata + episodes */
export const loadShow = createAsyncThunk(
  'analyze13/loadShow',
  async (rssUrl, { rejectWithValue }) => {
    try {
      const response = await fetch('/api/top-podcasts/analyze/rss', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ rssUrl, episodeLimit: EPISODE_LIMIT }),
      });
      const json = await response.json();
      if (!json.ok) throw new Error(json.error || 'Failed to parse RSS feed');
      return { rssUrl, data: json.parsed };
    } catch (err) {
      return rejectWithValue(err.message);
    }
  }
);

/** Run a single analysis module */
export const runModule = createAsyncThunk(
  'analyze13/runModule',
  async ({ moduleId, rssUrl, episodeIndex }, { rejectWithValue, getState }) => {
    try {
      const backendId = MODULE_ID_MAP[moduleId] || moduleId;

      // ── OP3 Downloads ─────────────────────────────────────────────────────
      if (backendId === 'op3_downloads') {
        const response = await fetch('/api/top-podcasts/op3-downloads', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ rssUrl, windowDays: 30 }),
        });
        const data = await response.json();
        return { moduleId, data };
      }

      // ── Legacy Social Scan (web search-based, kept for backward compat) ───
      if (backendId === 'social_scan') {
        const dataset = getState().analyze13.showData;
        const response = await fetch('/api/top-podcasts/social-scan', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ dataset, perQueryResults: 6 }),
        });
        const json = await response.json();
        if (!json.ok) throw new Error(json.error || 'Social scan failed');
        return { moduleId, data: json.result };
      }

      // ── All other modules (db-first) ──────────────────────────────────────
      const bodyPayload = { rssUrl, episodeLimit: EPISODE_LIMIT };
      if (episodeIndex != null) bodyPayload.episodeIndex = episodeIndex;
      const response = await fetch(`/api/top-podcasts/db-analyze/${backendId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(bodyPayload),
      });
      const json = await response.json();
      if (!json.ok) throw new Error(json.error || 'Analysis failed');
      return { moduleId, data: json.result };
    } catch (err) {
      return rejectWithValue({ moduleId, message: err.message });
    }
  }
);

/** Run all modules for the current track in parallel batches */
const RUN_ALL_BATCH_SIZE = 3;

export const runAllModules = createAsyncThunk(
  'analyze13/runAllModules',
  async ({ moduleIds, rssUrl }, { dispatch }) => {
    for (let i = 0; i < moduleIds.length; i += RUN_ALL_BATCH_SIZE) {
      const batch = moduleIds.slice(i, i + RUN_ALL_BATCH_SIZE);
      await Promise.all(batch.map((moduleId) => dispatch(runModule({ moduleId, rssUrl }))));
      // brief pause between batches to avoid overwhelming the server
      if (i + RUN_ALL_BATCH_SIZE < moduleIds.length) {
        await new Promise((r) => setTimeout(r, 200));
      }
    }
  }
);

/** Check cache / freshness status — no dedicated endpoint, kept as a no-op */
export const checkCacheStatus = createAsyncThunk(
  'analyze13/checkCacheStatus',
  async () => null
);

/** Force-refresh: re-parse the RSS feed to pick up new episodes */
export const refreshCache = createAsyncThunk(
  'analyze13/refreshCache',
  async (rssUrl, { dispatch }) => {
    await dispatch(loadShow(rssUrl));
  }
);

/** Export PDF — calls the server PDF export endpoint and triggers browser download */
export const exportPDF = createAsyncThunk(
  'analyze13/exportPDF',
  async ({ rssUrl, track = 'owner' }, { rejectWithValue }) => {
    try {
      const response = await fetch('/api/top-podcasts/pdf-export', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ rssUrl, track }),
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.error || `PDF export failed (${response.status})`);
      }
      // Trigger browser download from the returned binary blob
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `mowpod-report-${track}-${Date.now()}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      return null;
    } catch (err) {
      return rejectWithValue(err.message);
    }
  }
);

// ---------------------------------------------------------------------------
// Slice
// ---------------------------------------------------------------------------

const analyze13Slice = createSlice({
  name: 'analyze13',
  initialState: {
    // RSS / show
    currentRssUrl: null,
    showData: null,
    loadingShow: false,

    // Track & module navigation
    currentTrack: 'owner',   // 'owner' | 'research'
    currentModule: 'keyword_analyzer',

    // Per-module results keyed by moduleId
    analysisResults: {},
    // Per-module run timestamps (ms since epoch)
    moduleTimestamps: {},
    // Per-module loading flags
    loadingModules: {},
    // Per-module error messages
    moduleErrors: {},
    // Running-all flag
    runningAll: false,

    // Cache / freshness
    cacheStatus: null,

    // PDF export
    exportingPDF: false,

    // Global error
    error: null,
  },
  reducers: {
    setCurrentTrack(state, action) {
      state.currentTrack = action.payload;
    },
    setCurrentModule(state, action) {
      state.currentModule = action.payload;
    },
    clearResults(state) {
      state.analysisResults = {};
      state.moduleErrors = {};
      state.loadingModules = {};
      state.moduleTimestamps = {};
    },
    clearShowData(state) {
      state.currentRssUrl = null;
      state.showData = null;
      state.analysisResults = {};
      state.moduleErrors = {};
      state.loadingModules = {};
      state.moduleTimestamps = {};
      state.runningAll = false;
      state.cacheStatus = null;
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    // loadShow
    builder
      .addCase(loadShow.pending, (state) => {
        state.loadingShow = true;
        state.error = null;
        state.showData = null;
      })
      .addCase(loadShow.fulfilled, (state, action) => {
        state.loadingShow = false;
        state.currentRssUrl = action.payload.rssUrl;
        state.showData = action.payload.data;
        // Reset analysis when a new show is loaded
        state.analysisResults = {};
        state.moduleErrors = {};
        state.loadingModules = {};
        state.moduleTimestamps = {};
        state.cacheStatus = null;
      })
      .addCase(loadShow.rejected, (state, action) => {
        state.loadingShow = false;
        state.error = action.payload || 'Failed to load show';
      });

    // runModule
    builder
      .addCase(runModule.pending, (state, action) => {
        const { moduleId } = action.meta.arg;
        state.loadingModules[moduleId] = true;
        delete state.moduleErrors[moduleId];
      })
      .addCase(runModule.fulfilled, (state, action) => {
        const { moduleId, data } = action.payload;
        state.loadingModules[moduleId] = false;
        state.analysisResults[moduleId] = data;
        state.moduleTimestamps[moduleId] = Date.now();
      })
      .addCase(runModule.rejected, (state, action) => {
        const { moduleId, message } = action.payload || {};
        if (moduleId) {
          state.loadingModules[moduleId] = false;
          state.moduleErrors[moduleId] = message || 'Module failed';
        }
      });

    // runAllModules
    builder
      .addCase(runAllModules.pending, (state) => {
        state.runningAll = true;
      })
      .addCase(runAllModules.fulfilled, (state) => {
        state.runningAll = false;
      })
      .addCase(runAllModules.rejected, (state) => {
        state.runningAll = false;
      });

    // checkCacheStatus
    builder
      .addCase(checkCacheStatus.fulfilled, (state, action) => {
        state.cacheStatus = action.payload;
      });

    // exportPDF
    builder
      .addCase(exportPDF.pending, (state) => {
        state.exportingPDF = true;
      })
      .addCase(exportPDF.fulfilled, (state) => {
        state.exportingPDF = false;
      })
      .addCase(exportPDF.rejected, (state, action) => {
        state.exportingPDF = false;
        state.error = action.payload || 'Export failed';
      });
  },
});

export const {
  setCurrentTrack,
  setCurrentModule,
  clearResults,
  clearShowData,
} = analyze13Slice.actions;

export default analyze13Slice.reducer;
